import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Calculator, AlertTriangle, Sparkles, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none";

const SEISMIC_ZONES = ['I', 'II', 'III', 'IV', 'V'];

const DEFAULT_SPEC = {
  length: 60, width: 30, eave_height: 9, ridge_height: 11.5,
  clear_span: 30, bay_spacing: 6, roof_slope_deg: 5,
  num_internal_column_lines: 0, internal_span_m: '',
  connections_separate_line: true,
  eot_crane_capacity: 0, eot_cranes: [], hanging_load_kgsqm: 25, wind_speed: 39,
  seismic_zone: 'II',
  mezzanine_area_sqm: 0, mezzanine_loading_kgsqm: 500, mezzanine_levels: 0, mezzanines: [],
  sheeting_roof_grade: 'TR 0.5mm', wall_height_open_pct: 0,
  wall_cladding_sill_m: 0, sheet_lap_wastage_pct: 10,
  design_codes: ['IS-800'],
  steel_grade_mpa: 350,
};

// Building dimensions the AI must NEVER fabricate — if not found in the document
// they stay BLANK for manual entry. All other params fall back to standard values.
const DIM_FIELDS = ['length', 'width', 'eave_height', 'clear_span', 'ridge_height', 'bay_spacing', 'roof_slope_deg'];

// Merge AI-extracted values onto the defaults: keep dimension fields blank when the
// AI returned null (never guess), but let non-dimension params use standard defaults.
function mergeInitial(init) {
  const merged = { ...DEFAULT_SPEC };
  if (!init) return merged;
  for (const [k, v] of Object.entries(init)) {
    if (v === null || v === undefined) {
      if (DIM_FIELDS.includes(k)) merged[k] = '';   // blank dimension — user fills it
    } else {
      merged[k] = v;
    }
  }
  return merged;
}

const num = (v, d) => (v === '' || v == null || isNaN(Number(v)) ? d : Number(v));

export default function PEBTonnageCalc({ initialSpec, source }) {
  const [s, setS] = useState(() => mergeInitial(initialSpec));
  const [codeOpts, setCodeOpts] = useState([]);
  const [windOpts, setWindOpts] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [city, setCity] = useState('');
  const [windSource, setWindSource] = useState(null);

  const lookupWind = async () => {
    const q = (city || '').trim();
    if (!q) { setWindSource(null); return; }
    try {
      const r = await apiGet(`/peb-tonnage/wind-speed?city=${encodeURIComponent(q)}`);
      setS(prev => ({ ...prev, wind_speed: r.wind_speed_ms }));
      setWindSource(r.source === 'default'
        ? { found: false, text: `not found — using ${r.wind_speed_ms} m/s default` }
        : { found: true, text: `auto-set from ${q}, IS-875` });
    } catch { setWindSource(null); }
  };

  useEffect(() => {
    apiGet('/peb-tonnage/codes')
      .then(d => {
        setCodeOpts(d.design_codes || []);
        setWindOpts(d.wind_zones || []);
      })
      .catch(() => {});
  }, []);

  // Re-sync when AI extractor pushes a new initialSpec
  useEffect(() => {
    if (initialSpec) setS(mergeInitial(initialSpec));
  }, [initialSpec]);

  // Auto-compute (debounced) whenever spec changes
  useEffect(() => {
    const t = setTimeout(async () => {
      // Don't compute (or fabricate) until the essential dimensions are entered.
      const dimsReady = num(s.length, 0) > 0 && num(s.width, 0) > 0 && num(s.eave_height, 0) > 0;
      if (!dimsReady) { setResult(null); return; }
      setLoading(true);
      try {
        const payload = {
          length: num(s.length, 0),
          width: num(s.width, 0),
          eave_height: num(s.eave_height, 0),
          ridge_height: s.ridge_height === '' || s.ridge_height == null ? null : Number(s.ridge_height),
          clear_span: num(s.clear_span, num(s.width, 0)),
          bay_spacing: num(s.bay_spacing, 6),
          roof_slope_deg: num(s.roof_slope_deg, 5),
          num_internal_column_lines: Math.max(0, parseInt(s.num_internal_column_lines, 10) || 0),
          internal_span_m: s.internal_span_m === '' || s.internal_span_m == null ? null : Number(s.internal_span_m),
          hanging_load_kgsqm: num(s.hanging_load_kgsqm, 25),
          wind_speed: num(s.wind_speed, 39),
          seismic_zone: s.seismic_zone || 'II',
          sheeting_roof_grade: s.sheeting_roof_grade || 'TR 0.5mm',
          wall_height_open_pct: num(s.wall_height_open_pct, 0),
          wall_cladding_sill_m: num(s.wall_cladding_sill_m, 0),
          sheet_lap_wastage_pct: num(s.sheet_lap_wastage_pct, 10),
          steel_grade_mpa: num(s.steel_grade_mpa, 350),
          connections_separate_line: s.connections_separate_line !== false,
          design_codes: (s.design_codes && s.design_codes.length) ? s.design_codes : ['IS-800'],
          mezzanine_area_sqm: num(s.mezzanine_area_sqm, 0),
          mezzanine_loading_kgsqm: num(s.mezzanine_loading_kgsqm, 500),
          mezzanine_levels: num(s.mezzanine_levels, 0),
          // Sanitise crane rows — empty span / travel inputs must go to the API as null.
          eot_cranes: (s.eot_cranes || []).map((c) => ({
            capacity_t: Number(c.capacity_t) || 0,
            span_m: c.span_m === '' || c.span_m == null ? null : Number(c.span_m),
            travel_distance_m: c.travel_distance_m === '' || c.travel_distance_m == null ? null : Number(c.travel_distance_m),
          })),
          mezzanines: (s.mezzanines || []).map((m) => ({
            length_m: Number(m.length_m) || 0,
            width_m: Number(m.width_m) || 0,
            height_m: m.height_m === '' || m.height_m == null ? null : Number(m.height_m),
            loading_kgsqm: Number(m.loading_kgsqm) || 500,
          })),
        };
        const r = await apiPost('/peb-tonnage/compute', payload);
        setResult(r);
      } catch (e) {
        // Skip noisy toasts here — input flick is common
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [s]);

  const update = (k, v) => setS(prev => ({ ...prev, [k]: typeof prev[k] === 'number' ? (Number(v) || 0) : v }));
  const updateNum = (k, v) => setS(prev => ({ ...prev, [k]: Number(v) || 0 }));
  const addCrane = () => setS(prev => ({ ...prev, eot_cranes: [...(prev.eot_cranes || []), { capacity_t: 0, span_m: '', travel_distance_m: '' }] }));
  const removeCrane = (i) => setS(prev => ({ ...prev, eot_cranes: (prev.eot_cranes || []).filter((_, idx) => idx !== i) }));
  const updateCrane = (i, k, v) => setS(prev => ({
    ...prev,
    eot_cranes: (prev.eot_cranes || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c),
  }));
  const addMezz = () => setS(prev => ({ ...prev, mezzanines: [...(prev.mezzanines || []), { length_m: 0, width_m: 0, height_m: '', loading_kgsqm: 500 }] }));
  const removeMezz = (i) => setS(prev => ({ ...prev, mezzanines: (prev.mezzanines || []).filter((_, idx) => idx !== i) }));
  const updateMezz = (i, k, v) => setS(prev => ({
    ...prev,
    mezzanines: (prev.mezzanines || []).map((m, idx) => idx === i ? { ...m, [k]: v } : m),
  }));
  const toggleCode = (code) => {
    setS(prev => {
      const cur = new Set(prev.design_codes || []);
      if (cur.has(code)) cur.delete(code); else cur.add(code);
      const arr = Array.from(cur);
      return { ...prev, design_codes: arr.length ? arr : ['IS-800'] };
    });
  };

  return (
    <div className="space-y-6" data-testid="peb-calc">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#3b4872] mb-1.5 flex items-center gap-1.5">
          <Calculator size={12} /> PEB Tonnage Calculator
        </div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">Auto-estimate steel tonnage from a specification sheet</h1>
        <p className="text-xs text-slate-600 mt-1">
          Code-aware rule-of-thumb estimator for PEB tender quoting. Pick the design codes the client expects — the calculator adjusts the tonnage accordingly. For final design, refer to IS-800 / IS-875 / AISC-360 / MBMA.
        </p>
        {source && (
          <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-1.5 inline-flex items-center gap-1.5">
            <Sparkles size={12} /> Pre-filled from <strong>{source}</strong> via AI Tonnage Extractor.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Inputs */}
        <div className="lg:col-span-1 space-y-4">
          <Section title="Building Dimensions">
            <NumInput label="Length (m)" v={s.length} onChange={(v) => updateNum('length', v)} testid="peb-length" />
            <NumInput label="Width (m)" v={s.width} onChange={(v) => updateNum('width', v)} testid="peb-width" />
            <NumInput label="Eave Height (m)" v={s.eave_height} onChange={(v) => updateNum('eave_height', v)} testid="peb-eave" />
            <NumInput label="Ridge Height (m)" v={s.ridge_height} onChange={(v) => updateNum('ridge_height', v)} />
            <NumInput label="Clear Span (m)" v={s.clear_span} onChange={(v) => updateNum('clear_span', v)} testid="peb-clear-span" />
            <NumInput label="Bay Spacing (m)" v={s.bay_spacing} onChange={(v) => updateNum('bay_spacing', v)} />
            <NumInput label="Roof Slope (°)" v={s.roof_slope_deg} onChange={(v) => updateNum('roof_slope_deg', v)} step="0.5" />
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Internal column rows (0 = single clear span)</label>
              <input type="number" min="0" max="6" step="1" value={s.num_internal_column_lines ?? 0}
                onChange={(e) => setS(prev => ({ ...prev, num_internal_column_lines: Math.max(0, Math.min(6, parseInt(e.target.value, 10) || 0)) }))}
                className={inputCls} data-testid="peb-internal-lines" />
              <p className="text-[11px] text-slate-500 mt-1">Set to 1 for two-span, 2 for three-span (e.g. factory with 2 rows of internal columns running full length). Each internal column row significantly increases primary steel.</p>
            </div>
            {Number(s.num_internal_column_lines) > 0 && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Individual span (m)</label>
                <input type="number" min="0" step="0.1"
                  value={s.internal_span_m === '' || s.internal_span_m == null
                    ? (num(s.width, 0) / (Number(s.num_internal_column_lines) + 1)).toFixed(2)
                    : s.internal_span_m}
                  onChange={(e) => setS(prev => ({ ...prev, internal_span_m: e.target.value }))}
                  className={inputCls} data-testid="peb-individual-span" />
                <p className="text-[11px] text-slate-500 mt-1">Auto-computed as width ÷ (rows + 1) = {(num(s.width, 0) / (Number(s.num_internal_column_lines) + 1)).toFixed(2)} m. Edit if spans are unequal.</p>
              </div>
            )}
          </Section>

          <Section title="EOT Cranes">
            <p className="text-[11px] text-slate-500 -mt-1">An EOT crane is <strong>procured, not fabricated</strong> — its weight & lifting load are carried by the structure, so each crane <strong>increases the primary (mainframe) steel</strong>. Add a card per crane with its capacity, span and travel (runway) length.</p>
            {(s.eot_cranes || []).length === 0 && (
              <div className="text-[11px] text-slate-400 italic py-1" data-testid="peb-no-cranes">No cranes added.</div>
            )}
            {(s.eot_cranes || []).map((c, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-2.5 space-y-2 bg-slate-50/60" data-testid={`peb-crane-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872]">Crane {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeCrane(i)}
                    className="text-slate-400 hover:text-rose-600"
                    data-testid={`peb-crane-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <CraneInput label="Capacity (Tons)" v={c.capacity_t} onChange={(v) => updateCrane(i, 'capacity_t', v === '' ? 0 : Number(v))} testid={`peb-crane-cap-${i}`} />
                <div className="grid grid-cols-2 gap-2">
                  <CraneInput label="Span (m)" v={c.span_m} onChange={(v) => updateCrane(i, 'span_m', v)} placeholder="opt." testid={`peb-crane-span-${i}`} />
                  <CraneInput label="Travel Dist. (m)" v={c.travel_distance_m} onChange={(v) => updateCrane(i, 'travel_distance_m', v)} placeholder="opt." testid={`peb-crane-travel-${i}`} />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addCrane}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-display font-semibold text-[#3b4872] border border-dashed border-[#3b4872]/40 rounded-sm py-2 hover:bg-indigo-50"
              data-testid="peb-add-crane"
            >
              <Plus size={13} /> Add EOT Crane
            </button>
          </Section>

          <Section title="Loading">
            <NumInput label="Hanging Load (kg/sqm)" v={s.hanging_load_kgsqm} onChange={(v) => updateNum('hanging_load_kgsqm', v)} />
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">City / Location</label>
              <input type="text" value={city} placeholder="e.g. Nashik"
                onChange={(e) => setCity(e.target.value)} onBlur={lookupWind}
                className={inputCls} data-testid="peb-city" />
              {windSource && (
                <p className={`text-[11px] mt-1 ${windSource.found ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="peb-city-windnote">
                  ({windSource.text})
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Wind Speed (m/s)</label>
              <select value={s.wind_speed} onChange={(e) => { updateNum('wind_speed', e.target.value); setWindSource(null); }} className={inputCls + ' bg-white'} data-testid="peb-wind">
                {windOpts.length === 0 ? <option value={s.wind_speed}>{s.wind_speed} m/s</option> : windOpts.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Seismic Zone</label>
              <select value={s.seismic_zone} onChange={(e) => update('seismic_zone', e.target.value)} className={inputCls + ' bg-white'} data-testid="peb-seismic">
                {SEISMIC_ZONES.map(z => <option key={z} value={z}>Zone {z}</option>)}
              </select>
            </div>
            <NumInput label="Steel Grade Fy (MPa)" v={s.steel_grade_mpa} onChange={(v) => updateNum('steel_grade_mpa', v)} testid="peb-steel-grade" />
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="peb-connections-toggle">
              <input type="checkbox" checked={s.connections_separate_line !== false} className="mt-0.5"
                onChange={(e) => setS(prev => ({ ...prev, connections_separate_line: e.target.checked }))} />
              <span className="text-[11px] text-slate-600">Connections as a separate BOQ line (8.5%). Uncheck for vendor BOQs that embed connections inside primary (then primary is ~8% heavier and no separate connections line is shown).</span>
            </label>
          </Section>

          <Section title="Mezzanine Floors (optional)">
            <p className="text-[11px] text-slate-500 -mt-1">Add a card per mezzanine floor — each can have its own length, width, clear height and live load. Leave height blank if unknown.</p>
            {(s.mezzanines || []).length === 0 && (
              <div className="text-[11px] text-slate-400 italic py-1" data-testid="peb-no-mezz">No mezzanine floors added.</div>
            )}
            {(s.mezzanines || []).map((m, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-2.5 space-y-2 bg-slate-50/60" data-testid={`peb-mezz-card-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872]">Mezzanine {i + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeMezz(i)}
                    className="text-slate-400 hover:text-rose-600"
                    data-testid={`peb-mezz-remove-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <CraneInput label="Length (m)" v={m.length_m} onChange={(v) => updateMezz(i, 'length_m', v === '' ? 0 : Number(v))} testid={`peb-mezz-length-${i}`} />
                  <CraneInput label="Width (m)" v={m.width_m} onChange={(v) => updateMezz(i, 'width_m', v === '' ? 0 : Number(v))} testid={`peb-mezz-width-${i}`} />
                  <CraneInput label="Clear Height (m)" v={m.height_m} onChange={(v) => updateMezz(i, 'height_m', v)} placeholder="opt." testid={`peb-mezz-height-${i}`} />
                  <CraneInput label="Live Load (kg/sqm)" v={m.loading_kgsqm} onChange={(v) => updateMezz(i, 'loading_kgsqm', v === '' ? 0 : Number(v))} testid={`peb-mezz-load-${i}`} />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addMezz}
              className="w-full flex items-center justify-center gap-1.5 text-xs font-display font-semibold text-[#3b4872] border border-dashed border-[#3b4872]/40 rounded-sm py-2 hover:bg-indigo-50"
              data-testid="peb-add-mezz"
            >
              <Plus size={13} /> Add Mezzanine Floor
            </button>
          </Section>

          <Section title="Sheeting & Cladding">
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Roof Sheet</label>
              <select value={s.sheeting_roof_grade} onChange={(e) => update('sheeting_roof_grade', e.target.value)} className={inputCls + ' bg-white'} data-testid="peb-roof-grade">
                <option>TR 0.47mm</option>
                <option>TR 0.5mm</option>
                <option>TR 0.55mm</option>
                <option>TR 0.6mm</option>
                <option>Standing Seam 0.5mm</option>
              </select>
            </div>
            <NumInput label="Brick / Dwarf Wall Height (m)" v={s.wall_cladding_sill_m} onChange={(v) => updateNum('wall_cladding_sill_m', v)} step="0.5" testid="peb-dwarf-wall" />
            <p className="text-[11px] text-slate-500 -mt-1">Steel wall cladding is counted only ABOVE this masonry/brick base (0 = full-height steel walls).</p>
            <NumInput label="Sheet Lap & Wastage (%)" v={s.sheet_lap_wastage_pct} onChange={(v) => updateNum('sheet_lap_wastage_pct', v)} step="1" testid="peb-lap-wastage" />
            <p className="text-[11px] text-slate-500 -mt-1">Extra steel for side/end laps + cutting offcuts (typical 8–12%).</p>
            <NumInput label="Wall Open % (no sheeting)" v={s.wall_height_open_pct} onChange={(v) => updateNum('wall_height_open_pct', v)} step="5" />
          </Section>

          <Section title="Design Codes (pick all that apply)">
            <p className="text-[11px] text-slate-500 mb-2 -mt-1">When several codes are selected, the <strong>heaviest factor governs</strong> the tonnage (most conservative for tender budgeting).</p>
            <div className="grid grid-cols-2 gap-1.5">
              {codeOpts.map(c => {
                const checked = (s.design_codes || []).includes(c.code);
                return (
                  <label key={c.code} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-sm cursor-pointer border ${checked ? 'border-[#3b4872] bg-indigo-50 text-[#3b4872] font-semibold' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCode(c.code)}
                      data-testid={`peb-code-${c.code.toLowerCase()}`}
                    />
                    <span>{c.code}</span>
                    <span className="ml-auto text-[10px] opacity-70">×{c.factor.toFixed(2)}</span>
                  </label>
                );
              })}
            </div>
          </Section>
        </div>

        {/* Result */}
        <div className="lg:col-span-2 space-y-4">
          {!result && (
            <div className="border border-dashed border-slate-300 rounded-sm p-8 text-center" data-testid="peb-awaiting-dims">
              <Calculator size={22} className="mx-auto text-slate-300 mb-2" />
              <div className="font-display font-bold text-slate-700">Enter the building dimensions to estimate tonnage</div>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">Length, width and eave height are required. Nothing is assumed — fill them in and the tonnage updates automatically.</p>
            </div>
          )}
          {result && (
            <>
              <div className="bg-gradient-to-br from-[#3b4872] to-[#2e3a5d] text-white rounded-sm p-6" data-testid="peb-total">
                <div className="text-[10px] uppercase tracking-widest font-display font-bold opacity-70">Total Estimated Tonnage</div>
                <div className="font-display text-5xl font-extrabold mt-1">{result.total_tonnes.toFixed(2)} T</div>
                <div className="text-xs opacity-80 mt-2">
                  Built-up Area · {result.built_up_sqm.toLocaleString('en-IN')} sqm · {(result.total_tonnes * 1000 / result.built_up_sqm).toFixed(2)} kg per sqm
                </div>
                <div className="mt-3 text-xs grid grid-cols-2 gap-2 opacity-90">
                  <div>Steel only: <span className="font-bold">{result.steel_only_tonnes.toFixed(2)} T</span></div>
                  <div>+ Sheeting: <span className="font-bold">{result.sheeting_tonnes.toFixed(2)} T</span></div>
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-widest font-display opacity-90 border-t border-white/15 pt-3 flex flex-wrap gap-3">
                  <div>Applied Code: <span className="font-bold">{result.applied_code}</span> ×{result.design_code_factor.toFixed(2)}</div>
                  <div>Seismic Factor: <span className="font-bold">×{result.seismic_factor.toFixed(2)}</span></div>
                  {loading && <div className="opacity-70">re-computing…</div>}
                </div>
                {result.num_internal_column_lines > 0 && (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-display font-bold bg-amber-400/90 text-amber-950 px-2.5 py-1 rounded-sm" data-testid="peb-multispan-badge">
                    <AlertTriangle size={12} /> Multi-span: {result.num_internal_column_lines} internal column {result.num_internal_column_lines === 1 ? 'line' : 'lines'} · {result.individual_span_m}m each
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                  <div className="font-display font-bold text-sm">Breakdown</div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Element</th>
                      <th className="text-right px-4 py-2 font-semibold">Rate (kg/sqm)</th>
                      <th className="text-right px-4 py-2 font-semibold">Weight</th>
                      <th className="text-right px-4 py-2 font-semibold">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    <Row label={`Primary Steel (Columns, Rafters)${result.crane_count > 0 ? ` · incl. cranes (${result.crane_count})` : ''}`} rate={result.primary_rate_kg_sqm} tonnes={result.primary_tonnes} total={result.total_tonnes} testid="peb-primary" />
                    {result.num_internal_column_lines > 0 && result.internal_column_tonnes > 0 && (
                      <tr className="text-[11px] text-slate-500" data-testid="peb-internal-col-note">
                        <td className="px-4 py-1 pl-8 italic">↳ Internal columns ({result.num_internal_column_lines} row{result.num_internal_column_lines === 1 ? '' : 's'}, {result.individual_span_m}m span)</td>
                        <td className="px-4 py-1 text-right">—</td>
                        <td className="px-4 py-1 text-right tabular-nums">{result.internal_column_tonnes.toFixed(2)} T</td>
                        <td className="px-4 py-1 text-right">—</td>
                      </tr>
                    )}
                    {result.crane_count > 0 && (
                      <tr className="text-[11px] text-slate-500" data-testid="peb-crane-beam-note">
                        <td className="px-4 py-1 pl-8 italic">↳ Crane runway beams + brackets ({result.crane_count} crane{result.crane_count === 1 ? '' : 's'})</td>
                        <td className="px-4 py-1 text-right">—</td>
                        <td className="px-4 py-1 text-right tabular-nums">{result.crane_beam_tonnes.toFixed(2)} T</td>
                        <td className="px-4 py-1 text-right">—</td>
                      </tr>
                    )}
                    <Row label="Secondary Steel (Purlins, Girts, Struts)" rate={result.secondary_rate_kg_sqm} tonnes={result.secondary_tonnes} total={result.total_tonnes} testid="peb-secondary" />
                    <Row label="Bracing" rate={result.bracing_rate_kg_sqm} tonnes={result.bracing_tonnes} total={result.total_tonnes} />
                    {result.mezzanine_tonnes > 0 && (
                      <Row label={`Mezzanine Steel${result.mezzanine_count > 1 ? ` (${result.mezzanine_count} floors)` : ''}`} rate={null} tonnes={result.mezzanine_tonnes} total={result.total_tonnes} testid="peb-mezz-row" />
                    )}
                    {result.connections_tonnes > 0 && (
                      <Row label="Connections & Misc (8.5%)" rate={null} tonnes={result.connections_tonnes} total={result.total_tonnes} />
                    )}
                    <Row label="Roof Sheeting" rate={null} tonnes={result.roof_sheeting_tonnes} total={result.total_tonnes} testid="peb-roof-sheeting" />
                    <Row label="Wall Cladding (above dwarf wall)" rate={null} tonnes={result.wall_cladding_tonnes} total={result.total_tonnes} testid="peb-wall-cladding" />
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-display font-bold border-t border-slate-200">
                      <td className="px-4 py-2.5">TOTAL</td>
                      <td className="px-4 py-2.5 text-right">—</td>
                      <td className="px-4 py-2.5 text-right text-base">{result.total_tonnes.toFixed(2)} T</td>
                      <td className="px-4 py-2.5 text-right">100%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                <div className="flex items-start gap-2 text-xs text-amber-900">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold mb-1">Disclaimer · Budget estimation only</div>
                    These figures use industry rule-of-thumb formulas + a design-code adjustment factor (IS-800 baseline, AISC-360 −4%, EN-1993 +3%, etc.). Actual tonnage depends on detailed design per the chosen code (IS-800 / IS-875 / IS-1893 / AISC-360 / MBMA). Use for tender / preliminary budgeting only. Final tonnage should be confirmed by a structural engineer.
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NumInput({ label, v, onChange, step, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      <input type="number" step={step || '1'} value={v ?? ''} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid} />
    </div>
  );
}

function CraneInput({ label, v, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[9px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      <input
        type="number"
        min="0"
        step="any"
        value={v ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls + ' py-1.5'}
        data-testid={testid}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
      <div className="text-[10px] font-display font-bold uppercase tracking-widest text-[#3b4872]">{title}</div>
      {children}
    </div>
  );
}

function Row({ label, rate, tonnes, total, testid }) {
  const share = total > 0 ? (tonnes / total * 100) : 0;
  return (
    <tr className="border-t border-slate-100" data-testid={testid}>
      <td className="px-4 py-2">{label}</td>
      <td className="px-4 py-2 text-right text-xs">{rate != null ? rate.toFixed(2) : '—'}</td>
      <td className="px-4 py-2 text-right font-mono">{tonnes.toFixed(2)} T</td>
      <td className="px-4 py-2 text-right text-xs">{share.toFixed(1)}%</td>
    </tr>
  );
}
